// Utils
import { createSmartSelector } from '../../utils/milanoteReselect/milanoteReselect';
import {
    getAsyncResourceEntityState,
    getAsyncResourcesTypeState,
} from '../../utils/services/http/asyncResource/asyncResourceSelector';
import { prop } from '../../../common/utils/immutableHelper';
import {
    getColorComboList,
    getPhysicalId,
    getUsedColorCombos,
} from '../../../common/elements/utils/elementPropertyUtils';
import { generateColorComboList } from '../../../common/colors/colorComboUtils';
import {
    isAsyncEntityError,
    isAsyncEntityFetched,
    isAsyncEntityFetching,
} from '../../utils/services/http/asyncResource/asyncResourceUtils';

import { getCurrentVisibleBoardId, getCurrentBoardId } from '../../reducers/currentBoardId/currentBoardIdSelector';
import currentBoardSuggestedColorsSelector from '../../workspace/toolbar/selectionTools/colorTools/currentBoardSuggestedColorsSelector';

// Constants
import { ResourceTypes } from '../../utils/services/http/asyncResource/asyncResourceConstants';
import { DEFAULT_COLORS_ARRAY } from './boardActions';

export const currentBoardIdSelector = (state, props) => getCurrentVisibleBoardId(state, props) || props.currentBoardId;

const getElementIdFromElement = (state, ownProps) => getPhysicalId(ownProps.element);

// RESOURCES
export const boardResourcesSelector = getAsyncResourcesTypeState(ResourceTypes.boards);

// Current / current visible board resource state
export const getCurrentVisibleBoardResourceState = (state, props) =>
    getAsyncResourceEntityState(state, ResourceTypes.boards, currentBoardIdSelector(state, props));
export const getCurrentBoardResourceState = (state, props) =>
    getAsyncResourceEntityState(state, ResourceTypes.boards, getCurrentBoardId(state, props));

// Current visible board resource state getters
export const getIsCurrentVisibleBoardFetching = (state, props) =>
    isAsyncEntityFetching(getCurrentVisibleBoardResourceState(state, props));
export const getIsCurrentVisibleBoardFetched = (state, props) =>
    isAsyncEntityFetched(getCurrentVisibleBoardResourceState(state, props));
export const getIsCurrentVisibleBoardError = (state, props) =>
    isAsyncEntityFetched(getCurrentVisibleBoardResourceState(state, props));
export const getCurrentVisibleBoardError = (state, props) =>
    prop('error', getCurrentVisibleBoardResourceState(state, props));

export const getFetchedBoardIds = createSmartSelector(boardResourcesSelector, (boardResourcesState) =>
    Object.values(boardResourcesState).filter(isAsyncEntityFetched).map(prop('id')),
);

export const getFailedBoardIds = createSmartSelector(boardResourcesSelector, (boardResourcesState) =>
    Object.values(boardResourcesState).filter(isAsyncEntityError).map(prop('id')),
);

export const getBoardResourceEntityState = (state, ownProps) =>
    getAsyncResourceEntityState(state, ResourceTypes.boards, getElementIdFromElement(state, ownProps));

export const getIsBoardFetched = (state, ownProps) =>
    isAsyncEntityFetched(getBoardResourceEntityState(state, ownProps));
export const getIsBoardFetching = (state, ownProps) =>
    isAsyncEntityFetching(getBoardResourceEntityState(state, ownProps));
export const getIsBoardFetchError = (state, ownProps) =>
    isAsyncEntityError(getBoardResourceEntityState(state, ownProps));

// STATE
export const getCurrentBoard = (state, ownProps) => state.getIn(['elements', currentBoardIdSelector(state, ownProps)]);

export const getCurrentBoardInboxState = (state, ownProps) => {
    const currentBoard = getCurrentBoard(state, ownProps);
    return currentBoard && currentBoard.getIn(['content', 'inbox']);
};

// Get colorCombo palette related values from app state
export const getCurrentSuggestedColors = (state) => state.getIn(['app', 'colorComboPalette', 'currentSuggestedColors']);
export const getCurrentUsedColorCombos = (state) => state.getIn(['app', 'colorComboPalette', 'currentUsedColorCombos']);

export const getCurrentBoardColorComboList = (state, ownProps) => {
    const currentBoard = getCurrentBoard(state, ownProps);
    return getColorComboList(currentBoard);
};

export const getCurrentBoardUsedColorCombos = (state, ownProps) => {
    const currentBoard = getCurrentBoard(state, ownProps);
    return getUsedColorCombos(currentBoard);
};

export const getIsCurrentBoardRoot = (state, ownProps) => {
    const currentBoard = getCurrentBoard(state, ownProps);
    return currentBoard && !!currentBoard.getIn(['location', 'rootBoard']);
};

/**
 * Get the suggested colors, pass them into the color combo list generator
 * @returns new color combo list
 */
export const generateColorComboPalette = (state) => {
    const suggestedColors = currentBoardSuggestedColorsSelector(state);
    const colorList = suggestedColors?.length ? suggestedColors : DEFAULT_COLORS_ARRAY;
    return generateColorComboList(colorList);
};
